"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useTranslations } from "next-intl";
import { type FormEvent, useEffect, useRef, useState } from "react";

/** Mirrors Core's server-side resend limit (40s → 409 phoneVerifyCooldown). */
const RESEND_COOLDOWN_SECONDS = 40;

/** A network failure resolves to `null` so callers branch on status only. */
async function postOrNull(url: string, init?: RequestInit) {
  try {
    return await fetch(url, init);
  } catch {
    return null;
  }
}

interface TwoFactorStepProps {
  eventId: string;
  /** Redacted phone number from the login response, e.g. "+47*****678". */
  phoneNumber: string | null;
  /** Called once the code is verified and the session cookies are set. */
  onAuthenticated: () => void;
}

/**
 * Two-step verification gate after a `2fa_required` login. The SMS is already
 * sent at that point, so the resend cooldown starts at mount. No design node
 * exists — layout mirrors LoginStep. A single plain input (not segmented
 * boxes) keeps `one-time-code` SMS autofill and paste working; `text-base`
 * stops iOS Safari zooming the field on focus.
 */
export default function TwoFactorStep({
  eventId,
  phoneNumber,
  onAuthenticated,
}: TwoFactorStepProps) {
  const t = useTranslations("publicEventSignup");
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH ?? "";

  const [code, setCode] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  // Prompt token gone (cookie/JWT expired) — only a fresh login can recover.
  const [expired, setExpired] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resent, setResent] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);

  // A response landing after unmount must not navigate the funnel.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Lifetime interval: the decrement clamps at zero, a resend resets the counter.
  useEffect(() => {
    const timer = setInterval(
      () => setCooldown((s) => (s > 0 ? s - 1 : s)),
      1000,
    );
    return () => clearInterval(timer);
  }, []);

  function applyVerifyResponse(res: Response | null) {
    if (res?.ok) {
      onAuthenticated();
      return;
    }
    if (res?.status === 401) {
      setExpired(true);
      return;
    }
    if (res?.status === 403) {
      setFormError(t("twoFactorErrorWrongCode"));
      return;
    }
    setFormError(t("twoFactorErrorGeneric"));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);

    if (code.trim().length === 0) {
      setFieldError(t("twoFactorCodeRequired"));
      return;
    }
    setFieldError(null);

    setSubmitting(true);
    try {
      const res = await postOrNull(
        `${basePath}/api/events/${eventId}/signup/login/2fa`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code: code.trim() }),
        },
      );
      if (isMountedRef.current) applyVerifyResponse(res);
    } catch {
      if (isMountedRef.current) setFormError(t("twoFactorErrorGeneric"));
    } finally {
      if (isMountedRef.current) setSubmitting(false);
    }
  }

  async function handleResend() {
    // Concurrent resends could race the server-side token rotation.
    if (resending) return;
    setResending(true);
    setResent(false);
    setFormError(null);
    try {
      const res = await postOrNull(
        `${basePath}/api/events/${eventId}/signup/login/2fa/resend`,
        { method: "POST" },
      );
      if (!isMountedRef.current) return;

      if (!res) {
        setFormError(t("twoFactorErrorGeneric"));
        return;
      }
      if (res.ok) {
        setResent(true);
        setCooldown(RESEND_COOLDOWN_SECONDS);
        return;
      }
      if (res.status === 429) {
        // Mirror the server-side cooldown that refused the resend.
        setCooldown(RESEND_COOLDOWN_SECONDS);
        return;
      }
      if (res.status === 401) {
        setExpired(true);
        return;
      }
      setFormError(t("twoFactorErrorResendFailed"));
    } finally {
      if (isMountedRef.current) setResending(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex h-full flex-col gap-4"
      noValidate
    >
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold">{t("twoFactorHeader")}</h2>
        <p className="text-sm text-muted-foreground">
          {phoneNumber
            ? t("twoFactorBody", { phoneNumber })
            : t("twoFactorBodyNoPhone")}
        </p>
      </div>

      <Separator />

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="signup-2fa-code">{t("twoFactorCodeLabel")}</Label>
        <Input
          id="signup-2fa-code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={6}
          className="text-base"
          value={code}
          disabled={expired}
          aria-invalid={!!fieldError || !!formError}
          aria-describedby={
            fieldError
              ? "signup-2fa-code-error"
              : formError
                ? "signup-2fa-form-error"
                : undefined
          }
          onChange={(e) => setCode(e.target.value)}
        />
        {fieldError && (
          <p id="signup-2fa-code-error" className="text-sm text-destructive">
            {fieldError}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <button
          type="button"
          onClick={handleResend}
          disabled={cooldown > 0 || expired || resending}
          className="self-start text-sm font-medium text-foreground underline disabled:no-underline disabled:text-muted-foreground"
        >
          {cooldown > 0
            ? t("twoFactorResendWait", { seconds: cooldown })
            : t("twoFactorResend")}
        </button>
        {/* Polite live region: announced without stealing focus from the input. */}
        <p role="status" className="text-sm text-muted-foreground">
          {resent ? t("twoFactorResent") : ""}
        </p>
      </div>

      {/* CTA pinned to the sheet bottom, matching LoginStep. */}
      <div className="mt-auto flex flex-col gap-3 pt-4">
        {formError && (
          <p
            id="signup-2fa-form-error"
            className="text-sm text-destructive"
            role="alert"
          >
            {formError}
          </p>
        )}
        {expired && (
          <p className="text-sm text-destructive" role="alert">
            {t("twoFactorErrorExpired")}
          </p>
        )}

        <Button
          type="submit"
          variant="default"
          disabled={submitting || expired}
          className="w-full rounded-full"
        >
          {t("twoFactorSubmit")}
        </Button>
      </div>
    </form>
  );
}
